export const ARES_TIME_ZONE = 'America/Mexico_City';

type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const NAIVE_ISO_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;

function toWallClockDate(parts: DateParts): Date {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second));
}

function extractTimeZoneParts(date: Date, timeZone: string): DateParts {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const pick = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return {
    year: pick('year'),
    month: pick('month'),
    day: pick('day'),
    hour: pick('hour'),
    minute: pick('minute'),
    second: pick('second'),
  };
}

export function toAresWallClockDate(raw: string | Date | null | undefined): Date | null {
  if (!raw) return null;

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : toWallClockDate(extractTimeZoneParts(raw, ARES_TIME_ZONE));
  }

  const text = raw.trim();
  if (!text) return null;

  const naiveMatch = text.match(NAIVE_ISO_RE);
  if (naiveMatch) {
    return toWallClockDate({
      year: Number(naiveMatch[1]),
      month: Number(naiveMatch[2]),
      day: Number(naiveMatch[3]),
      hour: Number(naiveMatch[4]),
      minute: Number(naiveMatch[5]),
      second: Number(naiveMatch[6] ?? 0),
    });
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return toWallClockDate(extractTimeZoneParts(parsed, ARES_TIME_ZONE));
}

export function getAresNowWallClockDate(): Date {
  return toWallClockDate(extractTimeZoneParts(new Date(), ARES_TIME_ZONE));
}

export function formatAresDateTime(raw: string | Date | null | undefined): { date: string; time: string } {
  const date = toAresWallClockDate(raw);
  if (!date) return { date: '-', time: '-' };

  const nowYear = getAresNowWallClockDate().getUTCFullYear();
  const includeYear = date.getUTCFullYear() !== nowYear;
  const day = date.toLocaleDateString('es-MX', {
    day: '2-digit',
    timeZone: 'UTC',
  });
  const month = date
    .toLocaleDateString('es-MX', {
      month: 'short',
      timeZone: 'UTC',
    })
    .replace('.', '');
  const yearSuffix = includeYear ? `-${String(date.getUTCFullYear()).slice(-2)}` : '';

  return {
    date: `${day}-${month}${yearSuffix}`,
    time: date.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    }),
  };
}

export function formatAresDateTimeLabel(
  raw: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = toAresWallClockDate(raw);
  if (!date) return '--';
  return date.toLocaleString('es-MX', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
    ...options,
  });
}

export function getAresWallClockMs(raw: string | Date | null | undefined): number | null {
  const date = toAresWallClockDate(raw);
  return date ? date.getTime() : null;
}
